import { useRef } from 'react'
import { Outlet } from 'react-router-dom'
import { useRelay, RelayContext, useMe, type RelayCallbacks } from '../api'
import { useSidebarPrefs, type DesktopPosition, type MobilePosition } from '../lib/sidebarPrefs'
import { NotificationProvider, useNotify } from './Notifications'
import { Sidebar } from './Sidebar'
import { MobileNav } from './MobileNav'
import { DockSidebar } from './dock/DockSidebar'
import { DOCK_OUTER_OFFSET } from './dock/Dock'

export function AuthLayout() {
  return (
    <NotificationProvider>
      <AuthLayoutInner />
    </NotificationProvider>
  )
}

// Tailwind v4 JIT can't infer arbitrary class names built from string
// concatenation. Listing the full class names keeps the safelist obvious
// and the compiler happy. The pixel value (`80px` here) mirrors
// DOCK_OUTER_OFFSET — kept in sync with a runtime check below.
const desktopPadding: Record<DesktopPosition, string> = {
  left: 'md:pl-[80px]',
  right: 'md:pr-[80px]',
  top: 'md:pt-[80px]',
  bottom: 'md:pb-[80px]',
}
const mobilePadding: Record<MobilePosition, string> = {
  top: 'pt-[80px]',
  bottom: 'pb-[80px]',
}

function AuthLayoutInner() {
  const { data: me } = useMe()
  const { prefs } = useSidebarPrefs(me?.id)
  const dockMode = me?.role === 'admin' && prefs.style === 'dock'

  // Defence-in-depth: if Dock constants ever drift past the hard-coded
  // padding, we want a loud signal in dev rather than silent overlap.
  if (import.meta.env.DEV && DOCK_OUTER_OFFSET > 80) {
    console.warn(
      `[AuthLayout] DOCK_OUTER_OFFSET=${DOCK_OUTER_OFFSET}px exceeds layout padding (80px)`,
    )
  }

  const mainPadding = dockMode
    ? `${mobilePadding[prefs.mobilePosition]} md:pt-0 md:pb-0 ${desktopPadding[prefs.desktopPosition]}`
    : 'pb-16 md:pb-0'

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      {!dockMode && <Sidebar />}
      <main className={`flex-1 overflow-y-auto ${mainPadding}`}>
        {/*
          Admins don't need the relay WS: in the hub, `GetBrowser(userID)` for
          an admin returns nil (no phones attached), so the relay never pushes
          live events to them. Mounting useRelay would spawn read/ping/write
          goroutines and an extra TCP socket for nothing. Operators still need
          live phone_status / call_state events via the relay.

          While /me is pending we render nothing in main — RequireAdminAuth
          (the guard above this layout) already blocks the render until the
          role is known, so this is defence-in-depth rather than a new
          spinner.
        */}
        {me === undefined
          ? null
          : me.role === 'admin'
            ? <AdminContent />
            : <OperatorContent />}
      </main>
      {dockMode ? <DockSidebar /> : <MobileNav />}
    </div>
  )
}

function OperatorContent() {
  const notify = useNotify()
  const relay = useRelay(notify)
  return (
    <RelayContext.Provider value={relay}>
      <Outlet />
    </RelayContext.Provider>
  )
}

/**
 * Admin flow doesn't open a relay WS, but any child that calls `useRelaySend`
 * would crash without a provider. Supply stub refs so `wsRef.current` is null
 * (send branch hits the "not connected" toast, which admins can't trigger in
 * practice anyway — no dial UI in /staff).
 */
function AdminContent() {
  const wsRef = useRef<WebSocket | null>(null)
  const callbacksRef = useRef<RelayCallbacks>({})
  return (
    <RelayContext.Provider value={{ wsRef, callbacksRef }}>
      <Outlet />
    </RelayContext.Provider>
  )
}
